import React, { Children, cloneElement, isValidElement, ReactElement, ReactNode, useCallback } from 'react'

import { TimelineStatusItem, TimelineStatusItemProps } from '../TimelineStatusItem'
import * as Styled from './styles'

export interface TimelineStatusItemsListProps {
  children: ReactNode
  isCollapsed?: boolean
  isLoading?: boolean
  minVisibleItemsCount?: number
}

export const TimelineStatusItemsList = ({
  children,
  isCollapsed = false,
  isLoading = false,
  minVisibleItemsCount = 1,
}: TimelineStatusItemsListProps): ReactElement => {
  const shouldRenderItem = useCallback(
    (index: number) => !isCollapsed || index < minVisibleItemsCount,
    [isCollapsed, minVisibleItemsCount],
  )

  const shouldDisplaySeparator = (child: ReactElement<TimelineStatusItemProps>, index: number): boolean => {
    if (child.props.withSeparator === false) {
      return false
    }
    const childrenCount = Children.count(children)
    return childrenCount !== 1 && index < childrenCount - 1
  }

  return (
    <Styled.TimelineStatusItemsListWrapper>
      {isLoading ? (
        <Styled.TimelineStatusItemsListItem>
          <TimelineStatusItem isLoading />
        </Styled.TimelineStatusItemsListItem>
      ) : (
        Children.map(
          children,
          (child, index) =>
            isValidElement<TimelineStatusItemProps>(child) &&
            shouldRenderItem(index) && (
              <Styled.TimelineStatusItemsListItem key={index}>
                {cloneElement<TimelineStatusItemProps>(child, {
                  isCollapsed,
                  isLoading,
                  withSeparator: shouldDisplaySeparator(child, index),
                })}
              </Styled.TimelineStatusItemsListItem>
            ),
        )
      )}
    </Styled.TimelineStatusItemsListWrapper>
  )
}
